import { type MutableRefObject, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useTemplatePlacementOptional } from '@/contexts/template-placement-context'
import { useLiveAreaTopRight } from '@/hooks/use-live-area-top-right'
import { cn } from '@/utils/classNames'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

/**
 * Keeps a pointer or click on the prompt from reaching the canvas. The prompt renders in a portal,
 * but React still bubbles its events through the tree to the canvas it is declared in, where a
 * press on empty space starts a marquee, and that marquee's release clears the selection and
 * closes the right panel. Closing the Templates panel cancels the held placement, so Continue
 * found nothing to place.
 */
const keepFromCanvas = (event: { stopPropagation: () => void }) => event.stopPropagation()

const WARNING_TITLE = 'Place Template Over Existing Content?'
const WARNING_MESSAGE =
  'The template will be added on top of the current design. You can undo this action.'

type TemplateDropWarningProps = {
  shapeManagerRef: MutableRefObject<ShapeManagerRef | null>
}

/**
 * The warning for placing a template over a spread that already has elements on it (US-17): the
 * template would stack on top of them. It has two stages.
 *
 * While the drag is over the canvas (`isOverDropSurface`) it is a status message that shows and
 * hides with the drag-over state, never as a standing label, and never over a blank spread. The
 * pointer is mid-drag then, so it ignores the pointer and cannot change what the drag is over.
 *
 * Dropping on a spread with content holds the placement (`pendingPlacement`) and turns the message
 * into a confirmation with Cancel and Continue. Nothing is placed until Continue; Cancel and
 * Escape discard the drop. The placement provider closes it when the spread changes.
 *
 * Whether the spread has content is read from the live manager when the drag enters the canvas
 * (in an effect, not during render, where a ref read would go stale), so undoing the only element
 * makes the next drag warning-free. It is not re-read while the drag stays over the canvas. It
 * renders in a portal because the canvas children sit inside the zoom transform, which would
 * break `position: fixed`.
 */
export function TemplateDropWarning({ shapeManagerRef }: TemplateDropWarningProps) {
  const placement = useTemplatePlacementOptional()
  const isOver = placement?.isOverDropSurface === true
  const pending = placement?.pendingPlacement != null
  const cancelPendingPlacement = placement?.cancelPendingPlacement
  const [hasContent, setHasContent] = useState(false)
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setHasContent(isOver && (shapeManagerRef.current?.getShapes().length ?? 0) > 0)
  }, [isOver, shapeManagerRef])

  // The prompt takes keyboard focus so Escape and Tab work at once. Cancel is the default so a
  // held or repeated Enter from a keyboard drop cannot confirm it by accident.
  useEffect(() => {
    if (pending) {
      cancelButtonRef.current?.focus()
    }
  }, [pending])

  useEffect(() => {
    if (!pending || !cancelPendingPlacement) return

    // Capture phase, like the drag's own Escape (`use-placement-session`): an open tooltip or
    // popover would otherwise consume the key first. The key is spent here, so it does not also
    // clear the selection behind the prompt.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        cancelPendingPlacement()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)

    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [pending, cancelPendingPlacement])

  const show = pending || (isOver && hasContent)
  const { top, right } = useLiveAreaTopRight(show)

  if (!show) return null

  return createPortal(
    <Alert
      role={pending ? 'alertdialog' : 'status'}
      aria-live={pending ? undefined : 'polite'}
      aria-label={pending ? WARNING_TITLE : undefined}
      data-testid="template-drop-warning"
      className={cn(
        'fixed z-[60] flex w-[calc(100%-24px)] max-w-2xl items-start gap-3 border-orange-300 bg-orange-50 p-3 shadow-lg',
        pending ? 'pointer-events-auto' : 'pointer-events-none'
      )}
      style={{ top, right }}
      {...(pending
        ? {
            onPointerDown: keepFromCanvas,
            onPointerUp: keepFromCanvas,
            onMouseDown: keepFromCanvas,
            onClick: keepFromCanvas,
          }
        : {})}
    >
      <span className="shrink-0">
        <AlertTriangle aria-hidden="true" className="size-4 text-orange-600" />
      </span>
      <div className="min-w-0 flex-1">
        <AlertTitle className="text-orange-600">{WARNING_TITLE}</AlertTitle>
        <AlertDescription className="text-muted-foreground">{WARNING_MESSAGE}</AlertDescription>
      </div>
      {pending ? (
        <div className="flex shrink-0 items-center gap-2">
          <Button
            ref={cancelButtonRef}
            type="button"
            variant="outline"
            size="sm"
            onClick={placement?.cancelPendingPlacement}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={placement?.confirmPendingPlacement}
          >
            Continue
          </Button>
        </div>
      ) : null}
    </Alert>,
    document.body
  )
}
